/**
 * Markdown 消息处理器
 * 用于处理文本消息中的溯源链接、URL 转换和格式化
 */

/** 正则表达式，用于匹配 ^[数字]^ 模式的文本，用于处理溯源链接 */
const CITATION_REGEX = /\^(\[\d+\])+?\^/g

/**
 * URL 字符集：匹配 URL 中允许的字符
 * 排除：空格、中文、HTML 标签、各种括号和标点符号
 */
const URL_CHARS = '[^\\s\\u4e00-\\u9fa5<>()（）【】「」"\'、。，！？；：\\[\\]{}`]+'

/**
 * 处理溯源链接
 * 将 ^[数字]^ 格式的文本转换为可点击的Markdown链接
 */
function processCitationLinks(
  msg: string,
  citationsIsKnowledge: boolean,
  citations?: any[],
  internetCitations?: any[]
): string {
  try {
    return msg.replace(CITATION_REGEX, match => {
      // 没用溯源不显示溯源^[1][2]^这种数据
      if (!citationsIsKnowledge && (!internetCitations || internetCitations.length === 0)) {
        return ''
      }

      // 提取内部内容，如 ^[1][2]^ 中的 [1][2]
      const innerContent = match.slice(1, -1)

      // 将多个数字分割并转换为Markdown链接格式
      const numbers = innerContent.split('][').map(numStr => {
        const cleanedNumStr = numStr.replace(/^\[|\]$/g, '')
        const num = parseInt(cleanedNumStr, 10)

        if (citationsIsKnowledge) {
          if (isNaN(num) || !citations?.[num - 1]) {
            return `[${numStr}]`
          }
          return `[[${num}]](${citations[num - 1]?.url})`
        } else {
          if (isNaN(num) || !internetCitations?.[num - 1]) {
            return `[${numStr}]`
          }
          return `[[${num}]](${internetCitations[num - 1]?.url})`
        }
      })

      // 在多个链接之间添加空格，避免 ReactMarkdown 错误解析
      return numbers.join(' ')
    })
  } catch (error) {
    console.error('Error during citation link processing:', error)
    return msg
  }
}

/**
 * 修复格式错误的 URL
 * http:/ 或 https:/ 补全为 http:// 或 https://
 */
function fixMalformedUrls(msg: string): string {
  return msg.replace(/(https?):\/([^\/\s])/g, '$1://$2')
}

/**
 * 在 URL 和引用标记之间添加空格
 */
function addSpacesBeforeCitations(msg: string): string {
  // 在 URL 和 ^[数字]^ 之间添加空格
  msg = msg.replace(new RegExp(`(https?://${URL_CHARS})(\\^\\[\\d+\\]\\^)`, 'g'), '$1 $2')

  // 在 URL 和 [[数字]](url) 之间添加空格
  msg = msg.replace(new RegExp(`(https?://${URL_CHARS})(\\[\\[\\d+\\]\\]\\()`, 'g'), '$1 $2')

  return msg
}

/**
 * 标准化引用链接格式
 * 将 [[数字]](url) 格式转换为标准 Markdown 链接格式 [数字](url)
 */
function normalizeCitationLinks(msg: string): string {
  return msg.replace(/\[\[(\d+)\]\]\((https?:\/\/[^\)\s]+)\s*\)/g, (match, num, url) => {
    return `[${num}](${url.trim()})`
  })
}

/**
 * 处理嵌套的 Markdown 链接格式
 * [[URL](URL)](URL) -> [URL](URL)
 */
function fixNestedLinks(msg: string): string {
  // 处理 [[URL](URL)](URL) 格式
  msg = msg.replace(/\[\[(https?:\/\/[^\]]+)\]\]\((https?:\/\/[^\)]+)\)\]\((https?:\/\/[^\)]+)\)/g, '[$1]($3)')

  // 处理带空格的嵌套格式
  msg = msg.replace(/\[\[(https?:\/\/[^\]]+)\s*\]\((https?:\/\/[^\)]+)\s*\)\]\((https?:\/\/[^\)]+)\s*\)/g, '[$1]($3)')

  return msg
}

/**
 * 检查 URL 是否已经在 Markdown 链接中
 */
function isUrlInMarkdownLink(msg: string, offset: number, urlLength: number): boolean {
  const beforeUrl = msg.substring(Math.max(0, offset - 200), offset)
  const afterUrl = msg.substring(offset + urlLength, offset + urlLength + 50)

  // 检查是否在链接的 URL 部分中（前面有 ](）
  if (beforeUrl.includes('](')) {
    return true
  }

  // 检查是否在链接的文本部分中 [text](url)
  // 找到最后一个 [ 的位置
  const lastOpenBracket = msg.lastIndexOf('[', offset)
  if (lastOpenBracket !== -1) {
    // 检查从 [ 到当前位置之间是否有 ](
    const textBetweenBracket = msg.substring(lastOpenBracket, offset)
    // 如果后面有 ](，说明在链接文本部分
    if (afterUrl.includes('](')) {
      return true
    }
  }

  // 检查是否在 HTML 标签中
  if (beforeUrl.includes('>')) {
    return true
  }

  return false
}

/**
 * 检查 www. 域名是否应该被处理
 * 返回 true 表示不应该处理（已经在链接中或属于完整 URL）
 */
function shouldSkipWwwDomain(msg: string, offset: number): boolean {
  const beforeDomain = msg.substring(Math.max(0, offset - 200), offset)

  // 1. 如果前面有 ](，说明在链接的 URL 部分
  if (beforeDomain.includes('](')) {
    return true
  }

  // 2. 检查是否在链接的文本部分 [text](url)
  // 找到最后一个 [ 的位置
  const lastOpenBracket = msg.lastIndexOf('[', offset)
  if (lastOpenBracket !== -1) {
    // 检查从 [ 到当前位置之间的内容
    const textBetweenBracket = msg.substring(lastOpenBracket, offset)
    // 检查后面是否有 ](，如果有，说明可能在链接文本部分
    const nextCloseBracket = msg.indexOf('](', offset)
    if (nextCloseBracket !== -1) {
      // 只有当文本中包含协议（https:// 或 http://）时，才说明在链接文本部分
      // 这样可以避免误判，比如 www.baidu.com[[1]](...) 这种情况
      if (textBetweenBracket.includes('https://') || textBetweenBracket.includes('http://')) {
        return true
      }
    }
  }

  // 3. 如果前面有 https:// 或 http://，且没有分隔符，说明是完整 URL 的一部分
  const protocolMatch = beforeDomain.match(/(https?:\/\/)(.*)$/)
  if (protocolMatch) {
    const afterProtocol = protocolMatch[2]
    // 如果没有空格、换行、] 等分隔符，说明是 URL 的一部分
    if (!afterProtocol.match(/[\s\n\r\]\>]/)) {
      return true
    }
  }

  return false
}

/**
 * 将裸 URL 转换为 Markdown 链接格式 [URL](URL)
 * 包括 https://、http:// 和 www. 开头的域名
 */
function convertBareUrlsToLinks(msg: string): string {
  // 1. 先处理 https:// 和 http:// 的 URL
  const urlRegex = new RegExp(`(https?://${URL_CHARS})`, 'g')
  msg = msg.replace(urlRegex, (match, url, offset) => {
    // 检查 URL 是否已经在 Markdown 链接中
    if (isUrlInMarkdownLink(msg, offset, match.length)) {
      return match
    }

    const trimmedUrl = url.trim()
    const rawAfterUrl = msg.substring(offset + match.length)
    const afterUrlClean = rawAfterUrl.replace(/^\s+/, '')

    // 检查后面是否跟着引用链接
    const hasCitationAfter = afterUrlClean.match(/^\[\[?\d+\]\]?\(/)

    // 转换为 Markdown 链接格式
    if (hasCitationAfter) {
      return `[${trimmedUrl}](${trimmedUrl}) `
    }
    return `[${trimmedUrl}](${trimmedUrl})`
  })

  // 2. 处理 www. 开头的域名
  const wwwRegex = /(www\.[a-zA-Z0-9]([a-zA-Z0-9\-\.]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+)/g
  msg = msg.replace(wwwRegex, (match, domain, offset) => {
    // 检查是否应该跳过（已经在链接中或属于完整 URL）
    if (shouldSkipWwwDomain(msg, offset)) {
      return match
    }

    const rawAfterDomain = msg.substring(offset + match.length)
    const afterDomainClean = rawAfterDomain.replace(/^\s+/, '')

    // 检查后面是否跟着引用链接
    const hasCitationAfter = afterDomainClean.match(/^\[\[?\d+\]\]?\(/)

    // 转换为 Markdown 链接格式 [www.domain](www.domain)
    const fullUrl = `${domain}`
    if (hasCitationAfter) {
      return `[${domain}](${fullUrl}) `
    }
    return `[${domain}](${fullUrl})`
  })

  return msg
}

/**
 * 添加标点符号和链接之间的空格
 */
function addSpacesAroundPunctuation(msg: string): string {
  // 在标点符号后面如果直接跟着链接，添加空格
  msg = msg.replace(/([）\)\]。，、！？；：])(?=\[)/g, '$1 ')

  // 在 URL 后面如果直接跟着右括号，添加空格
  msg = msg.replace(new RegExp(`(https?://${URL_CHARS})([）\\)\\]])`, 'g'), '$1 $2')

  // 在中文文本、英文字母、数字后面如果直接跟着引用链接，添加空格
  msg = msg.replace(/([\u4e00-\u9fa5a-zA-Z0-9])(?=\[\d+\]\()/g, '$1 ')

  return msg
}

/**
 * 规范化链接和引用链接之间的空格和换行符
 * 确保它们在同一行显示，只有一个空格
 */
function normalizeLinkCitationSpacing(msg: string): string {
  // 去除链接和引用链接之间的标点符号和换行符
  msg = msg.replace(/(\]\(https?:\/\/[^\)]+\))\s*[。，、！？；：]\s*[\n\r]*\s*(\[\d+\]\()/g, '$1 $2')

  // 去除链接和引用链接之间的所有换行符
  msg = msg.replace(/(\]\(https?:\/\/[^\)]+\))\s*[\n\r]+\s*(\[\d+\]\()/g, '$1 $2')

  // 去除单个换行符（没有空格的情况）
  msg = msg.replace(/(\]\(https?:\/\/[^\)]+\))[\n\r]+(\[\d+\]\()/g, '$1 $2')

  // 去除多余空格（保留一个空格）
  msg = msg.replace(/(\]\(https?:\/\/[^\)]+\))\s{2,}(\[\d+\]\()/g, '$1 $2')

  // 最后统一处理：确保只有一个空格，没有换行符
  msg = msg.replace(/(\]\(https?:\/\/[^\)]+\))[\s\n\r]+(\[\d+\]\()/g, '$1 $2')

  return msg
}

/**
 * 处理文本消息的主函数
 * 按顺序执行所有处理步骤
 */
export function processMarkdownMessage(
  msg: string,
  options: {
    citationsIsKnowledge?: boolean
    citations?: any[]
    internetCitations?: any[]
    enableDebug?: boolean
  } = {}
): string {
  const { citationsIsKnowledge = false, citations, internetCitations, enableDebug = false } = options

  if (!msg) {
    return msg
  }

  try {
    // 1. 处理溯源链接
    msg = processCitationLinks(msg, citationsIsKnowledge, citations, internetCitations)

    if (enableDebug) {
      console.log('处理溯源链接后:', msg)
    }

    // 2. 修复格式错误的 URL
    msg = fixMalformedUrls(msg)

    // 3. 在 URL 和引用标记之间添加空格
    msg = addSpacesBeforeCitations(msg)

    // 4. 标准化引用链接格式
    msg = normalizeCitationLinks(msg)

    // 5. 处理嵌套的 Markdown 链接
    msg = fixNestedLinks(msg)

    // 6. 将裸 URL 转换为 Markdown 链接
    msg = convertBareUrlsToLinks(msg)

    // 7. 添加标点符号和链接之间的空格
    msg = addSpacesAroundPunctuation(msg)

    // 8. 规范化链接和引用链接之间的空格
    msg = normalizeLinkCitationSpacing(msg)

    if (enableDebug) {
      console.log('处理后的 msg:', msg)
      const linkCitationMatch = msg.match(/(\]\(https?:\/\/[^\)]+\))([\s\n\r]*)(\[\d+\]\()/g)
      if (linkCitationMatch) {
        console.log('链接和引用链接匹配:', linkCitationMatch)
      }
    }

    return msg
  } catch (error) {
    console.error('Error during markdown message processing:', error)
    return msg
  }
}
